import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { DefaultTheme } from 'styled-components';
import usePersistedState from '@/core/hooks/usePersistedState';
import { getTheme, DesignId, Mode } from './getTheme';

interface ThemeState {
	designId: DesignId;
	mode: Mode;
}

const initialState: ThemeState = { designId: 'default', mode: 'light' };

export interface ThemeContextProps {
	theme: DefaultTheme;
	designId: DesignId;
	mode: Mode;
	setDesignId: (id: DesignId) => void;
	toggleTheme: () => void;
}

// Экспортируем сам контекст (а не только провайдер), чтобы окружения
// вроде Storybook могли отдать в `useTheme` собственное значение темы,
// синхронизированное со своим переключателем, не поднимая боевой
// `CustomThemeProvider` с его persisted-состоянием.
export const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

/**
 * При смене темы styled-components переписывает className множеству узлов,
 * и любые `transition`-правила на этих узлах (в том числе глобальный
 * `transition: 220ms all ease-in-out` для `<button>` в `global.ts`)
 * начинают анимировать color/box-shadow/padding и т.п. На страницах с
 * большим количеством стилизованных кнопок (`MyWidgets`) это видно как
 * «дёрганье»: элементы аккордеона мгновенно растут и возвращаются назад
 * до того, как тема визуально применилась.
 *
 * Подавляем transitions на один кадр через атрибут `data-theme-changing`
 * на `<html>` — селектор `html[data-theme-changing] *` ставит
 * `transition: none !important` (см. `styles/global.ts`). После того,
 * как React закоммитил новый theme-context, на следующем кадре снимаем
 * атрибут — обычные transitions возвращаются к работе.
 */
const FLAG_ATTR = 'data-theme-changing';
const suppressTransitionsForOneFrame = (): void => {
	if (typeof document === 'undefined') return;
	const root = document.documentElement;
	root.setAttribute(FLAG_ATTR, '');
	// Двойной rAF: первый — дождаться layout/paint после смены theme,
	// второй — снять флаг уже на «спокойном» кадре.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			root.removeAttribute(FLAG_ATTR);
		});
	});
};

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [state, setState] = usePersistedState<ThemeState>('themeState', initialState);
	const theme = useMemo(() => getTheme(state.designId, state.mode), [state.designId, state.mode]);

	const setDesignId = useCallback((designId: DesignId) => {
		suppressTransitionsForOneFrame();
		setState((prev) => ({ ...prev, designId }));
	}, [setState]);

	const toggleTheme = useCallback(() => {
		suppressTransitionsForOneFrame();
		setState((prev) => ({ ...prev, mode: prev.mode === 'light' ? 'dark' : 'light' }));
	}, [setState]);

	// Синхронизация с ванильным переключателем темы (resources/js/bx/theme-switcher.js):
	// он шлёт 'tm:themechange' при переключении в той же вкладке, где usePersistedState
	// сам по себе изменение не увидел бы (читает localStorage только при монтировании).
	useEffect(() => {
		const onExternalChange = (e: Event): void => {
			const detail = (e as CustomEvent<Partial<ThemeState>>).detail;
			if (detail?.designId && detail?.mode) {
				suppressTransitionsForOneFrame();
				setState({ designId: detail.designId, mode: detail.mode });
			}
		};
		window.addEventListener('tm:themechange', onExternalChange);
		return () => window.removeEventListener('tm:themechange', onExternalChange);
	}, [setState]);

	const value = useMemo(() => ({
		theme,
		designId: state.designId,
		mode: state.mode,
		setDesignId,
		toggleTheme,
	}), [theme, state.designId, state.mode, setDesignId, toggleTheme]);

	return (
		<ThemeContext.Provider value={value}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = (): ThemeContextProps => {
	const context = useContext(ThemeContext);
	if (!context) throw new Error('useTheme must be used within a ThemeProvider');
	return context;
};
